/**
 * Owner: Masa Bayu
 * File: CommunityPostCard.tsx
 * Date:Time: 2026-10-17 04:14
 * Description: Dedicated community post card — like / bookmark / share wired to the real
 *   QXCircle client API. Profile palette.
 */

import { useState } from 'react';
import { LandingIcon } from '../../home/marketplace/landing/LandingIcons';
import {
  bookmarkQxCirclePost,
  likeQxCirclePost,
  shareQxCirclePost,
  type QxCircleFeedPost,
} from '../client/qxcircle-api';

export interface CommunityPostCardProps {
  readonly post: QxCircleFeedPost;
  readonly onNavigate: (path: string) => void;
  readonly onChanged: () => void;
}

function initials(name: string): string {
  const parts = name.trim().split(/\s+/);
  const first = parts[0]?.[0] ?? '?';
  const second = parts[1]?.[0] ?? '';
  return `${first}${second}`;
}

function whenLabel(iso: string): string {
  const then = Date.parse(iso);
  if (Number.isNaN(then)) return '';
  const diff = Date.now() - then;
  if (diff < 60_000) return 'just now';
  if (diff < 3_600_000) return `${String(Math.floor(diff / 60_000))}m`;
  if (diff < 86_400_000) return `${String(Math.floor(diff / 3_600_000))}h`;
  return `${String(Math.floor(diff / 86_400_000))}d`;
}

export function CommunityPostCard({ post, onNavigate, onChanged }: CommunityPostCardProps) {
  const [busy, setBusy] = useState(false);

  const run = async (action: () => Promise<unknown>) => {
    if (busy) return;
    setBusy(true);
    try {
      await action();
      onChanged();
    } catch {
      /* surface stays; API error tolerated */
    } finally {
      setBusy(false);
    }
  };

  return (
    <article className="cmc__card">
      <div className="cmc__card-head">
        <span className="cmc__avatar" aria-hidden="true">
          {initials(post.authorName)}
        </span>
        <span className="cmc__who">
          <span className="cmc__name">{post.authorName}</span>
          <span className="cmc__handle">@{post.authorHandle}</span>
        </span>
        <span className="cmc__time">{whenLabel(post.createdAt)}</span>
      </div>

      <p className="cmc__body">{post.body}</p>

      {post.media !== null ? (
        <img className="cmc__media" src={post.media.previewDataUrl} alt={post.media.fileName} />
      ) : null}

      <div className="cmc__card-actions">
        <button
          type="button"
          className="cmc__action"
          data-on={post.likedByLocal}
          disabled={busy}
          onClick={() => {
            void run(() => likeQxCirclePost(post.id));
          }}
        >
          <LandingIcon name="heart" size={14} /> {String(post.likeCount)}
        </button>
        <button
          type="button"
          className="cmc__action"
          onClick={() => {
            onNavigate(`/circle/post/${encodeURIComponent(post.id)}`);
          }}
        >
          <LandingIcon name="message" size={14} /> {String(post.commentCount)}
        </button>
        <button
          type="button"
          className="cmc__action"
          data-on={post.savedByLocal}
          disabled={busy}
          onClick={() => {
            void run(() => bookmarkQxCirclePost(post.id));
          }}
        >
          <LandingIcon name="bookmark" size={14} /> Save
        </button>
        <button
          type="button"
          className="cmc__action"
          disabled={busy}
          onClick={() => {
            void run(() => shareQxCirclePost(post.id));
          }}
        >
          <LandingIcon name="share" size={14} /> {String(post.shareCount)}
        </button>
        <button
          type="button"
          className="cmc__action"
          onClick={() => {
            onNavigate(`/circle/profile/${encodeURIComponent(post.authorHandle)}`);
          }}
        >
          Profile
        </button>
      </div>
    </article>
  );
}
